import { zIndex } from '@xemplo/zindex';

import { usePopoverContext } from '../../hooks/use-popover-context';
import { applyAsChild, isRenderProp } from '../../utils/popover.helpers';
import { ElementWithRef, RenderProp } from '../../utils/popover-helpers.types';

import * as S from './popover-popup.styles';

type PopoverPopupProps<T extends HTMLElement = HTMLElement> = {
  children: ElementWithRef<T> | RenderProp<T>;
  className?: string;
  asChild?: boolean;
};

export function PopoverPopup<T extends HTMLElement = HTMLElement>({
  children,
  className,
  asChild = false,
}: PopoverPopupProps<T>) {
  const {
    open,
    labelId,
    descriptionId,
    floatingStyles,
    setOpen,
    getFloatingProps,
    refs,
  } = usePopoverContext();

  const childNode = isRenderProp(children) ? children({ open, setOpen }) : children;

  if (!open) return null;

  const popupProps = {
    ...getFloatingProps(),
    role: 'dialog',
    'aria-labelledby': labelId,
    'aria-describedby': descriptionId,
    style: { ...floatingStyles, zIndex: zIndex.Popover },
  };

  if (asChild) {
    return applyAsChild<T>({
      asChild,
      children: childNode,
      ref: refs.setFloating,
      propsToInject: popupProps,
    });
  }

  return (
    <S.Container {...popupProps} className={className} ref={refs.setFloating}>
      {childNode}
    </S.Container>
  );
}
